import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  concatMap,
  distinctUntilChanged,
  first,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { fromRouter } from '@blaze-shared/app-state-data-access';
import { syncSelect } from '@blaze-shared/platform-data-access';
import { PageContext } from '@blaze-shared/projects-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { DatasetSchema, PageContextSchema } from '@blaze-shared/schema-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';

import { pageContextFeature } from '../+state/page-context.reducer';
import { selectPageContextByPageId } from '../+state/page-context.selectors';
import * as runtimeSelectors from '../../runtime/runtime.selectors';
import { createPageContextSchema } from '../create-page-context-schema';
import { logPageContext } from '../logger';
import { PAGE_TYPES_SUPPORTING_PAGE_CONTEXT } from '../models/consts';
import {
  PAGE_CONTEXT_DATA_ROW_ID_URL_ROUTE_PARAM,
  PAGE_CONTEXT_ID_URL_QUERY_PARAM,
} from '../models/url-params';

@Injectable({
  providedIn: 'root',
})
export class PageContextQueryService {
  private readonly store = inject(Store);
  private readonly schemaService = inject(SchemaService);

  getPageContextForPageId(pageId: string): Observable<PageContext | undefined> {
    // Log the request
    logPageContext('info', 'PageContextForPageService', 'observePageContextForPageId', (tag) =>
      console.log(`${tag} 📄 Page Context - observe request for page ${pageId}`),
    );

    // Emits once when the page context state is loaded
    const waitUntilPageContextLoaded$ = this.store
      .select(pageContextFeature.selectLoaded)
      .pipe(first((loaded) => loaded));

    // Wait until the page context state is loaded
    // and then get the page context for the given page ID
    return waitUntilPageContextLoaded$.pipe(
      concatLatestFrom(() => this.store.select(runtimeSelectors.selectPagesById(pageId))),
      concatMap(([, page]) => {
        // If page type missing or not supported (header, etc.) - no page context
        if (!page?.pageType || !PAGE_TYPES_SUPPORTING_PAGE_CONTEXT.includes(page.pageType)) {
          return of(undefined);
        }

        // Handle dialog-like pages (presented on an overlay)
        // There's always a single Page Context for a dialog-like page
        // and it's cleared once it's closed, so we can use the page ID to get it
        const isDialogLike = page.pageType === 'Dialog' || page.pageType === 'Drawer';
        if (isDialogLike) {
          return this.store.select(selectPageContextByPageId(pageId));
        }

        // Regular full page - the logic is more complex
        return this.getCurrentPageContext();
      }),

      // Don't emit the same page context multiple times
      distinctUntilChanged(isDeepEqual),

      // Log the result to the console
      tap((pageContext) => {
        logPageContext('info', 'PageContextForPageService', 'observePageContextForPageId', (tag) =>
          console.log(`${tag} 📄 Page Context - observe result for page ${pageId}`, {
            pageContext,
          }),
        );
      }),
    );
  }

  /**
   * Observes the Page Context schema for the given page ID.
   *
   * When a concrete Page Context is available (a data row is loaded), its schema is used.
   * Otherwise, for Detail Pages the schema is derived from the page's main dataset so that
   * `$$Page` and its columns are still available for intellisense in the editor, where no
   * data row is selected when navigating via the Pages side menu.
   */
  getPageContextSchemaForPageId(pageId: string): Observable<PageContextSchema | undefined> {
    return this.getPageContextForPageId(pageId).pipe(
      switchMap((pageContext) =>
        pageContext?.schema ? of(pageContext.schema) : this.getMainDatasetPageContextSchema(pageId),
      ),
    );
  }

  getCurrentPageRowIdSync(): string | undefined {
    return syncSelect(
      this.store,
      fromRouter.selectRouteParam(PAGE_CONTEXT_DATA_ROW_ID_URL_ROUTE_PARAM),
    );
  }

  getCurrentPageContextIdSync(): string | null {
    const pageContextId = syncSelect(
      this.store,
      fromRouter.selectQueryParam(PAGE_CONTEXT_ID_URL_QUERY_PARAM),
    );
    return this.normalizeUrlParamsValue(pageContextId) || null;
  }

  getCurrentPageContextId(): Observable<string | null> {
    return this.store.select(fromRouter.selectQueryParam(PAGE_CONTEXT_ID_URL_QUERY_PARAM)).pipe(
      map(this.normalizeUrlParamsValue),
      map((urlPageContextId) => urlPageContextId || null),
    );
  }

  getCurrentPageContext(): Observable<PageContext | undefined> {
    return combineLatest([
      this.getCurrentPageContextId(),
      this.store.select(fromRouter.selectRouteParam(PAGE_CONTEXT_DATA_ROW_ID_URL_ROUTE_PARAM)),
      this.store.select(pageContextFeature.selectPageContextEntities),
      this.store.select(runtimeSelectors.selectPageId),
    ]).pipe(
      map(([pageContextId, dataRowIdParam, entities, pageId]) => {
        if (pageContextId && entities[pageContextId]) {
          return entities[pageContextId];
        }

        // Cold open / missing ?pid: context rebuilt from route dataRowId under a new id.
        const dataRowId = this.normalizeUrlParamsValue(dataRowIdParam);
        if (!dataRowId || !pageId) {
          return undefined;
        }

        return Object.values(entities).find(
          (pageContext) => pageContext.pageId === pageId && pageContext.pageDataRowId === dataRowId,
        );
      }),
    );
  }

  getAllPageContexts(): Observable<Record<string, PageContext>> {
    return this.store.select(pageContextFeature.selectPageContextEntities);
  }

  private getMainDatasetPageContextSchema(
    pageId: string,
  ): Observable<PageContextSchema | undefined> {
    return this.store.select(runtimeSelectors.selectPagesById(pageId)).pipe(
      first(),
      switchMap((page) => {
        if (!page?.mainDatasetId) {
          return of(undefined);
        }
        return this.schemaService
          .obtainSchema<DatasetSchema>(page.mainDatasetId)
          .pipe(map((schema) => (schema ? createPageContextSchema(schema, pageId) : undefined)));
      }),
    );
  }

  private normalizeUrlParamsValue(params: string | string[] | undefined): string | undefined {
    return Array.isArray(params) ? params[0] : params;
  }
}
